/**
 * KioskPage – Always-On Ambient Dashboard + Tiered PIN Access
 *
 * States:
 *  AMBIENT  – No PIN. Shows live venue data (board, specials, stock 86, weather, matches).
 *             PIN overlay accessible via floating button. Data refreshes every 60s.
 *  SESSION  – After PIN success. KioskDashboard renders over this. 30s hard-logout
 *             returns here (not to blank PIN screen).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Monitor, LogIn, X, AlertCircle, Loader2,
  MessageSquare, ChefHat, RefreshCw, Clock,
} from 'lucide-react';
import { useKioskStore } from '../lib/kiosk';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import { kioskAPI } from '../lib/api';
import KioskDashboard from './KioskDashboard';

const DOTS = [0, 1, 2, 3];
const INACTIVITY_MS  = 30_000;
const AMBIENT_TTL_MS = 60_000; // re-fetch ambient data every 60s

// ─────────────────────────────────────────────
// Mini ambient display components (read-only)
// ─────────────────────────────────────────────

function AmbientClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="text-center select-none">
      <p className="text-[#FF6600] text-7xl font-black tabular-nums tracking-tight leading-none">
        {now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </p>
      <p className="text-[#71717A] text-sm mt-1">
        {now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </p>
    </div>
  );
}

function ApiSportsGamesWidget() {
  const apiSportsKey = process.env.REACT_APP_API_SPORTS_KEY || '';

  useEffect(() => {
    const id = 'api-sports-widgets-js';
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.id = id;
    script.type = 'module';
    script.src = 'https://widgets.api-sports.io/3.1.0/widgets.js';
    document.body.appendChild(script);
  }, []);

  if (!apiSportsKey) {
    return (
      <div className="bg-[#18181B]/80 border border-[#27272A] rounded-2xl px-4 py-3">
        <p className="text-[#71717A] text-xs">Set <code>REACT_APP_API_SPORTS_KEY</code> to enable today's fixtures widget.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#18181B]/80 border border-[#27272A] rounded-2xl px-2 py-2 space-y-2">
      <h2 className="text-[#FF6600] text-sm font-bold uppercase tracking-wide px-1">Today's Big Games</h2>
      <api-sports-widget
        data-type="config"
        data-key={apiSportsKey}
        data-sport="football"
        data-theme="dark"
        data-timezone="Europe/London"
        data-show-logos="true"
      />
      <api-sports-widget data-type="games" data-league="39" data-tab="today" data-show-toolbar="false" />
      <api-sports-widget data-type="games" data-league="40" data-tab="today" data-show-toolbar="false" />
    </div>
  );
}

function WeatherApiWidget() {
  const weatherKey = process.env.REACT_APP_WEATHER_API_KEY || '';
  const [weather, setWeather] = useState(null);
  const [weatherError, setWeatherError] = useState(false);

  useEffect(() => {
    if (!weatherKey) return;
    setWeather(null);
    setWeatherError(false);
    fetch(`https://api.weatherapi.com/v1/current.json?key=${weatherKey}&q=auto:ip`)
      .then((r) => r.json())
      .then((data) => setWeather(data))
      .catch(() => setWeatherError(true));
  }, [weatherKey]);

  if (!weatherKey) {
    return (
      <div className="bg-[#18181B]/80 border border-[#27272A] rounded-2xl px-4 py-3">
        <p className="text-[#71717A] text-xs">Set <code>REACT_APP_WEATHER_API_KEY</code> to enable live weather.</p>
      </div>
    );
  }

  if (weatherError) {
    return (
      <div className="bg-[#18181B]/80 border border-[#27272A] rounded-2xl px-4 py-3">
        <p className="text-[#71717A] text-xs">Weather unavailable.</p>
      </div>
    );
  }

  if (!weather) {
    return (
      <div className="bg-[#18181B]/80 border border-[#27272A] rounded-2xl px-4 py-3 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-[#71717A]" />
        <span className="text-[#71717A] text-xs">Detecting location…</span>
      </div>
    );
  }

  return (
    <div className="bg-[#18181B]/80 border border-[#27272A] rounded-2xl px-4 py-3 flex items-center justify-between">
      <div>
        <div className="text-white font-bold text-sm">{weather.location.name}</div>
        <div className="text-[#A1A1AA] text-xs">{weather.current.condition.text}</div>
      </div>
      <div className="flex items-center gap-2">
        <img src={`https:${weather.current.condition.icon}`} alt="" width={48} />
        <div className="text-white text-2xl font-bold">{Math.round(weather.current.temp_c)}°C</div>
      </div>
    </div>
  );
}

function AmbientBoard({ message }) {
  if (!message) return null;
  return (
    <div className="bg-[#18181B]/80 border border-[#FF6600]/20 rounded-2xl px-4 py-3">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="w-4 h-4 text-[#FF6600]" />
        <p className="text-[#FF6600] text-xs font-bold uppercase tracking-wide">Manager Board</p>
      </div>
      <p className="text-[#D1D1D1] text-sm whitespace-pre-line leading-relaxed">{message}</p>
    </div>
  );
}

function AmbientSpecials({ specials }) {
  if (!specials?.length) return null;
  return (
    <div className="bg-[#18181B]/80 border border-[#27272A] rounded-2xl px-4 py-3">
      <p className="text-[#FF6600] text-xs font-bold uppercase tracking-wide mb-2">Today's Specials</p>
      <div className="space-y-1">
        {specials.slice(0, 4).map((s, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-white text-sm font-medium">{s.name}</span>
            {s.price && <span className="text-[#FF6600] text-sm font-bold ml-2">£{s.price}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function AmbientStock86({ items }) {
  const off = items?.filter((i) => !i.available) || [];
  if (!off.length) return null;
  const kitchen = off.filter((i) => i.department === 'kitchen');
  const bar     = off.filter((i) => i.department === 'bar');
  return (
    <div className="bg-[#18181B]/80 border border-red-900/30 rounded-2xl px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <ChefHat className="w-4 h-4 text-red-400" />
        <p className="text-red-400 text-xs font-bold uppercase tracking-wide">86'd Items</p>
      </div>
      {kitchen.length > 0 && (
        <p className="text-[#71717A] text-xs mb-0.5">🍳 Kitchen: <span className="text-[#D1D1D1]">{kitchen.map((i) => i.name).join(', ')}</span></p>
      )}
      {bar.length > 0 && (
        <p className="text-[#71717A] text-xs">🍹 Bar: <span className="text-[#D1D1D1]">{bar.map((i) => i.name).join(', ')}</span></p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// PIN Overlay
// ─────────────────────────────────────────────

function PinOverlay({ venueId, onSuccess, onClose }) {
  const [pin, setPin]       = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const handleNum = (n) => {
    if (loading || pin.length >= 4) return;
    const next = pin + n;
    setPin(next);
    if (next.length === 4) submit(next);
  };

  const submit = async (p) => {
    setLoading(true);
    setError('');
    try {
      const res = await kioskAPI.pinLogin(p, venueId);
      onSuccess(res.data.user);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid PIN.';
      setError(msg);
      setTimeout(() => { setPin(''); setError(''); }, 1200);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end p-6 pointer-events-none">
      {/* Backdrop (left side) — semi-transparent, ambient still readable */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto" onClick={onClose} />

      <div className="relative pointer-events-auto w-72 bg-[#0F0F10] border border-[#FF6600]/30 rounded-3xl p-6 shadow-2xl shadow-black/80">
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 text-[#52525B] hover:text-white">
          <X className="w-4 h-4" />
        </button>

        <p className="text-[#FF6600] text-xs font-bold uppercase tracking-widest mb-1">Staff Login</p>
        <p className="text-[#52525B] text-xs mb-5">Enter your 4-digit PIN</p>

        {/* PIN dots */}
        <div className="flex justify-center gap-4 mb-5">
          {DOTS.map((i) => (
            <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all duration-100 ${
              i < pin.length ? 'bg-[#FF6600] border-[#FF6600] scale-110' : 'border-[#3F3F46]'
            }`} />
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-1 text-red-400 text-xs justify-center mb-3 animate-pulse">
            <AlertCircle className="w-3 h-3" />{error}
          </div>
        )}

        {/* Numpad */}
        <div className="grid grid-cols-3 gap-2 mb-2">
          {[1,2,3,4,5,6,7,8,9].map((n) => (
            <button key={n} onPointerDown={() => handleNum(String(n))} disabled={loading}
              className="h-14 bg-[#18181B] hover:bg-[#27272A] active:bg-[#FF6600] active:scale-95 rounded-xl text-2xl font-bold text-white transition-all border border-[#27272A] disabled:opacity-40">
              {n}
            </button>
          ))}
          <div />
          <button onPointerDown={() => handleNum('0')} disabled={loading}
            className="h-14 bg-[#18181B] hover:bg-[#27272A] active:bg-[#FF6600] active:scale-95 rounded-xl text-2xl font-bold text-white transition-all border border-[#27272A] disabled:opacity-40">
            0
          </button>
          <button onPointerDown={() => setPin((p) => p.slice(0, -1))} disabled={loading || !pin.length}
            className="h-14 bg-[#18181B] hover:bg-[#27272A] active:bg-red-900 active:scale-95 rounded-xl text-xl font-bold text-red-400 transition-all border border-[#27272A] disabled:opacity-30">
            ⌫
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 text-[#FF6600] text-xs mt-2">
            <Loader2 className="w-3 h-3 animate-spin" />Verifying…
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Root: KioskPage
// ─────────────────────────────────────────────

export default function KioskPage() {
  const {
    isKioskDevice, kioskVenueName, kioskVenueId,
    pinSessionActive, startPinSession, endPinSession,
  } = useKioskStore();
  const navigate = useNavigate();

  const [pinOpen, setPinOpen]       = useState(false);
  const [countdown, setCountdown]   = useState(null);
  const [ambient, setAmbient]       = useState(null);
  const [ambientLoading, setAmbientLoading] = useState(true);
  const refreshTimer = useRef(null);

  // Non-kiosk devices → standard login
  useEffect(() => {
    if (!isKioskDevice) navigate('/login', { replace: true });
  }, [isKioskDevice, navigate]);

  // Fetch ambient data (also used as refresh)
  const fetchAmbient = useCallback(async () => {
    if (!kioskVenueId) return;
    try {
      const r = await kioskAPI.getAmbientData(kioskVenueId);
      setAmbient(r.data);
    } catch {
      // silently retain stale data
    } finally {
      setAmbientLoading(false);
    }
  }, [kioskVenueId]);

  // Initial load + 60s refresh
  useEffect(() => {
    fetchAmbient();
    refreshTimer.current = setInterval(fetchAmbient, AMBIENT_TTL_MS);
    return () => clearInterval(refreshTimer.current);
  }, [fetchAmbient]);

  // ── Inactivity: HARD logout → revert to ambient (not blank) ──
  const handleTimeout = useCallback(() => {
    endPinSession();
    setPinOpen(false);
    setCountdown(null);
  }, [endPinSession]);

  const handleWarning = useCallback(() => {
    let secs = 5;
    setCountdown(secs);
    const iv = setInterval(() => {
      secs -= 1;
      if (secs <= 0) { clearInterval(iv); setCountdown(null); }
      else setCountdown(secs);
    }, 1000);
  }, []);

  useInactivityTimer({
    timeoutMs: INACTIVITY_MS,
    onTimeout: handleTimeout,
    onWarning: handleWarning,
    warningMs: 5_000,
    enabled: pinSessionActive,
  });

  // ── Active session overlay (countdown warning) ──
  if (pinSessionActive) {
    return (
      <>
        {countdown !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
               onClick={() => setCountdown(null)}>
            <div className="text-center pointer-events-none">
              <p className="text-white text-xl font-semibold mb-2">Session expiring in…</p>
              <p className="text-[#FF6600] text-9xl font-black tabular-nums">{countdown}</p>
              <p className="text-[#A1A1AA] text-sm mt-4">Tap anywhere to stay logged in</p>
            </div>
          </div>
        )}
        <KioskDashboard onLogout={() => { endPinSession(); setPinOpen(false); }} />
      </>
    );
  }

  // ── Ambient Dashboard ──
  return (
    <div className="min-h-screen bg-[#050507] text-white overflow-hidden relative select-none">

      {/* Subtle radial glow behind clock */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#FF6600]/5 rounded-full blur-3xl" />
      </div>

      {/* Header bar */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-5 pb-3 border-b border-[#18181B]">
        <div className="flex items-center gap-2">
          <Monitor className="w-4 h-4 text-[#FF6600]" />
          <span className="text-[#FF6600] text-xs font-bold uppercase tracking-widest">
            {kioskVenueName || 'Venue Kiosk'} · Terminal Mode
          </span>
        </div>
        <button onClick={fetchAmbient} className="text-[#3F3F46] hover:text-[#FF6600] transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Main ambient content */}
      <div className="relative z-10 flex flex-col items-center px-4 pt-8 pb-32 gap-6 max-w-xl mx-auto">
        {/* Clock — centrepiece */}
        <AmbientClock />

        {/* Loading shimmer */}
        {ambientLoading && (
          <div className="flex items-center gap-2 text-[#52525B] text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />Loading venue data…
          </div>
        )}

        <div className="w-full space-y-3">
          <ApiSportsGamesWidget />
          <WeatherApiWidget />
          <AmbientBoard    message={ambient?.board} />
          <AmbientSpecials specials={ambient?.specials} />
          <AmbientStock86  items={ambient?.stock86} />
        </div>
      </div>

      {/* PIN overlay trigger — bottom-right, always visible */}
      {!pinOpen && (
        <button
          onClick={() => setPinOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-[#FF6600] hover:bg-[#e55a00] active:scale-95 transition-all text-white font-bold text-sm px-5 py-3 rounded-2xl shadow-lg shadow-[#FF6600]/20">
          <LogIn className="w-4 h-4" />
          Staff Login
        </button>
      )}

      {/* PIN overlay — translucent, ambient visible behind it */}
      {pinOpen && (
        <PinOverlay
          venueId={kioskVenueId}
          onSuccess={(user) => {
            startPinSession({ userId: user.id, userName: user.name, role: user.role });
            setPinOpen(false);
          }}
          onClose={() => setPinOpen(false)}
        />
      )}

      <p className="fixed bottom-2 left-1/2 -translate-x-1/2 text-[#1C1C1E] text-xs z-10">
        Raise The Bar · Terminal Mode · v3
      </p>
    </div>
  );
}

